import * as React from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  BackgroundPolicy,
  backgroundPolicyOf,
  libraryAllowed,
  uploadsAllowed,
} from "@/services/bedrock/dtos/BackgroundAsset";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { useDesignPackageMutations } from "../hooks/useDesignPackageMutations";

const PUBLISHED_NOTE = "Applies to new posts right away. Posts already made aren't affected.";

type Extra = "library" | "upload";

// Two independent add-ons on top of the design's own backgrounds.
const isOn = (policy: BackgroundPolicy, extra: Extra) =>
  extra === "library" ? libraryAllowed(policy) : uploadsAllowed(policy);

function nextPolicy(policy: BackgroundPolicy, extra: Extra): BackgroundPolicy {
  const library = isOn(policy, "library") !== (extra === "library");
  const uploads = isOn(policy, "upload") !== (extra === "upload");
  return backgroundPolicyOf(library, uploads);
}

const EXTRAS: { extra: Extra; label: string }[] = [
  { extra: "library", label: "Your library" },
  { extra: "upload", label: "Their own photo" },
];

interface BackgroundPolicyControlProps {
  pkg: DesignPackageDetail;
  /** Viewers who can't edit see what's allowed without changing it. */
  canEdit: boolean;
}

/**
 * What players may use when they share this design, beyond its own backgrounds: one line of toggle
 * chips under the slide's tiles. The setting covers every slide.
 */
export const BackgroundPolicyControl: React.FC<BackgroundPolicyControlProps> = ({ pkg, canEdit }) => {
  const { setBackgroundPolicy } = useDesignPackageMutations(pkg.id);
  const policy = pkg.backgroundPolicy;
  const saving = setBackgroundPolicy.isPending;

  const toggle = (extra: Extra) => {
    setBackgroundPolicy.mutate(nextPolicy(policy, extra), {
      onSuccess: () => {
        if (pkg.publishedAt) toast.info(PUBLISHED_NOTE);
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="background-policy">
      <span id="background-policy-label">When sharing, players can also use:</span>
      <div role="group" aria-labelledby="background-policy-label" className="flex flex-wrap items-center gap-1.5">
        {EXTRAS.map(({ extra, label }) => {
          const on = isOn(policy, extra);
          const chipClass = cn(
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-medium transition-colors",
            on ? "border-primary bg-indigo-50 text-primary" : "border-slate-200 bg-white text-slate-600"
          );
          const content = (
            <>
              {on && <Check className="size-3" aria-hidden="true" />}
              {label}
            </>
          );
          return canEdit ? (
            <button
              key={extra}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(extra)}
              disabled={saving}
              className={cn(
                chipClass,
                "cursor-pointer hover:border-primary disabled:cursor-default disabled:opacity-60 disabled:hover:border-current"
              )}
            >
              {content}
            </button>
          ) : (
            <span key={extra} className={chipClass} data-state={on ? "on" : "off"}>
              {content}
            </span>
          );
        })}
      </div>
      {pkg.items.length > 1 && <span className="text-slate-400">· all slides</span>}
      {saving && (
        <span
          className="inline-flex items-center gap-1 text-slate-400"
          role="status"
          data-testid="background-policy-saving"
        >
          <Spinner className="size-3" />
          Saving…
        </span>
      )}
    </div>
  );
};
